import { get, set, del, keys, entries } from "idb-keyval";

export interface OfflineEntry {
  id: string;
  type: "sale_document" | "purchase_document" | "payment_transaction" | "voucher"
    | "jobwork_document" | "jobwork_bill" | "jobwork_done_bill"
    | "sale_conversion" | "purchase_conversion" | "purchase_consolidation"
    | "voucher_update" | "ledger_update" | "payment_update" | "bank_reconciliation"
    | "stock_audit_line_update";
  table: string;
  payload: Record<string, any>;
  createdAt: string;
  status: "pending" | "syncing" | "failed";
  errorMessage?: string;
  retryCount: number;
}

const QUEUE_PREFIX = "offline_q_";

/**
 * True from the moment a user switch is detected until the previous user's queue is wiped.
 * Replay must not run in that window (it would POST user A's entries under user B's session).
 */
let replayBlocked = false;
export function setReplayBlocked(blocked: boolean): void {
  replayBlocked = blocked;
}
export function isReplayBlocked(): boolean {
  return replayBlocked;
}

function queueKey(id: string) {
  return `${QUEUE_PREFIX}${id}`;
}

export async function addToQueue(entry: Omit<OfflineEntry, "id" | "createdAt" | "status" | "retryCount">): Promise<OfflineEntry> {
  const full: OfflineEntry = {
    ...entry,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    status: "pending",
    retryCount: 0,
  };
  await set(queueKey(full.id), full);
  return full;
}

export async function getQueue(): Promise<OfflineEntry[]> {
  const allEntries = await entries<string, OfflineEntry>();
  return allEntries
    .filter(([k]) => (k as string).startsWith(QUEUE_PREFIX))
    .map(([, v]) => v)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export async function getQueueCount(): Promise<number> {
  const allKeys = await keys<string>();
  return allKeys.filter((k) => (k as string).startsWith(QUEUE_PREFIX)).length;
}

export async function updateEntry(id: string, updates: Partial<OfflineEntry>): Promise<void> {
  const existing = await get<OfflineEntry>(queueKey(id));
  if (existing) {
    await set(queueKey(id), { ...existing, ...updates });
  }
}

export async function hasQueueEntry(id: string): Promise<boolean> {
  return (await get<OfflineEntry>(queueKey(id))) != null;
}

export async function removeFromQueue(id: string): Promise<void> {
  await del(queueKey(id));
}


/** Drops every queued entry. Used on logout / user switch so one user's unsynced work never replays under another. */
export async function clearQueue(): Promise<void> {
  const allKeys = await keys<string>();
  await Promise.all(allKeys.filter((k) => (k as string).startsWith(QUEUE_PREFIX)).map((k) => del(k)));
}
